<script lang="ts" module>
	type VacancyResponse = {
		data: {
			'?xml': {
				'@_version': string
				'@_encoding': string
			}
			rss: {
				channel: {
					title: string
					link: string
					description: string
					language: string
					pub_date: string
					items?: Array<{
						date: string
						title: string
						clientname: string
						location: string
						region: string
						reference: number
						category: string
						employment_type: string
						publish_type: string
						text_description: string
						pub_date: string
						exp_date: string
						app_form_closing_date: string
						link: string
					}>
				}
				'@_version': string
			}
		}
		last_fetched: string
		refreshed: boolean
	}

	type VacancyItems = VacancyResponse['data']['rss']['channel']['items']

	/** What this block's `load` attaches to the blok at request time. */
	type VacanciesData = { vacancies?: VacancyItems }

	async function get_vacancies(): Promise<VacancyItems> {
		const vacancies_url = new SvelteURL('https://xmljson.vercel.app')
		vacancies_url.searchParams.set('url', 'https://bedalesschool.ciphr-irecruit.com/rssfeed.aspx')
		const response = await fetch(vacancies_url)
		const data = (await response.json()) as VacancyResponse
		return data.data.rss.channel.items?.filter(v => v.publish_type !== 'Internal') ?? []
	}

	// results go on the blok, not module scope: module state is shared by every
	// request the server process handles, so one visitor's data leaked into
	// everyone else's page. `story` is per-request, so this is safe.
	export const load: Load = async ({ story }) => {
		if (!('blocks' in story.content)) return

		const blocks = story.content.blocks?.filter(b => b.component === 'vacancies') as
			| Array<VacanciesData>
			| undefined
		if (!blocks?.length) return

		const vacancies = await get_vacancies()

		for (const block of blocks) block.vacancies = vacancies
	}
</script>

<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Vacancies } from '#library/components.schema.build.ts'
	import Button from '#components/button.svelte'
	import Share from '#components/share.svelte'
	import { SvelteURL } from 'svelte/reactivity'

	interface Props {
		blok: Blok<Vacancies> & VacanciesData
	}

	let { blok }: Props = $props()

	let categories = $derived([...new Set(blok.vacancies?.map(v => v.category))])
	let current: string | undefined = $state()

	function get_apply_url(link: string): string {
		const match = link.match(/vacancy\/(\d+)/i)
		if (!match) return link
		const url = new URL(link)
		return `${url.origin}/Applicants/vacancy/apply/${match[1]}`
	}
</script>

<section class="vacancies {blok.theme}" {...attrs(blok)}>
	<div class="contain layout">
		{#if blok.vacancies?.length}
			<nav class="nav contain">
				{#each categories as category (category)}
					<button
						class="link"
						class:on={category === (current ?? categories[0])}
						onclick={() => (current = category)}>{category}</button
					>
				{/each}
			</nav>
			<a class="view-all" href="https://bedalesschool.ciphr-irecruit.com/Applicants/vacancy" target="_blank">
				View all vacancies
			</a>
			{#each blok.vacancies?.filter(v => v.category === (current ?? categories[0])) as { title, employment_type, text_description, link }, index (index)}
				<div class="panel">
					<div class="content">
						<div class="header">
							<div class="title">{title}</div>
							{#each employment_type.split('/') as tag (tag)}
								<div class="tag">{tag.trim()}</div>
							{/each}
						</div>
						<div class="body">{@html text_description}</div>
					</div>
					<div class="actions">
						<Button href={link} target="_blank" icon="arrow-up-right">View Vacancy</Button>
						<Button href={get_apply_url(link)} target="_blank" outline icon="arrow-up-right"
							>Apply now</Button
						>
						<Share {link} copy={title} alt />
					</div>
				</div>
			{/each}
		{:else}
			<div class="empty">
				<p>Sorry, we do not have any vacancies right now.</p>
				<a
					class="small link"
					href="https://bedalesschool.ciphr-irecruit.com/Applicants/account/register"
					target="_blank"
					rel="nofollow">Register here for job alerts</a
				>
			</div>
		{/if}
	</div>
</section>

<style lang="css">
	.vacancies {
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.nav {
		display: flex;
		gap: 1.5rem;
		overflow-x: auto;
		scrollbar-width: none;

		@media (--from-mobile) {
			flex-direction: row;
			gap: 3rem;

			&::-webkit-scrollbar {
				display: none;
			}
		}
	}

	.link {
		color: var(--c-midground);
		flex: 0 0 auto;
		font: 600 var(--f-h3);
		letter-spacing: var(--ls-h3);
		text-align: center;
		text-decoration: none;
		transition: font-weight 0.5s ease;
		white-space: nowrap;

		&:hover,
		&.on {
			color: var(--c-foreground);
			text-decoration: underline;
		}

		&.small {
			font: 600 var(--f-h6);
			letter-spacing: var(--ls-body1);
		}
	}

	.panel {
		background-color: var(--c-white);
		display: flex;
		flex-direction: column;
		gap: 2.5rem;
		padding: 2.5rem;

		@media (--from-mobile) {
			flex-direction: row;
			justify-content: space-between;
			gap: 1.5rem;
			padding: 3.75rem;
			grid-column: span 2;
		}
	}

	.content {
		@media (--from-mobile) {
			max-width: 52.5rem;
		}
	}

	.header {
		align-items: center;
		display: flex;
		gap: 0.5rem;
		justify-content: start;
		flex-wrap: wrap;
		margin-block-end: 1.5rem;

		@media (--from-mobile) {
			gap: 1rem;
		}
	}

	.title {
		font: 600 var(--f-h4);
		letter-spacing: var(--ls-h4);
	}

	.tag {
		align-items: center;
		background-color: var(--c-gimson);
		color: var(--c-crocus);
		display: flex;
		font: 500 var(--f-body2);
		letter-spacing: var(--ls-body2);
		padding: 0.3125rem 0.75rem;
		text-transform: uppercase;
	}

	.body {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 6;
		word-break: break-word;
		overflow: hidden;
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
	}

	.empty {
		text-align: center;
		font: 400 var(--f-body1);
		display: grid;
		gap: 0.5rem;
	}

	.actions {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;

		@media (--from-mobile) {
			max-width: 12.5rem;
		}

		:global(.share) {
			margin-block-start: 1.5rem;

			@media (--from-mobile) {
				margin-block-start: auto;
			}
		}
	}

	.view-all {
		color: var(--c-crocus);
		font: 500 var(--f-body1);
		letter-spacing: var(--ls-body1);
		order: 1;

		&:hover {
			text-decoration: underline;
		}

		@media (--from-mobile) {
			order: unset;
			margin-inline-start: auto;
		}
	}

	.layout {
		display: grid;
		gap: 1.5rem;

		@media (--from-mobile) {
			grid-template-columns: 1fr auto;
			align-items: center;
		}
	}
</style>
